// Shared betting settlement for the tournament TEAM games (Alliance, Cha Cha
// Cha, Stableford best-net). Each group is a team; teams compete across the
// whole field. Mirrors the individual pot / per-point / Nassau model used by
// Stroke Play & Stableford, lifted to the team level.
//
//   • Bet per Player (pot): every player antes `bet`; pot = field × bet; the
//     winning team splits the pot among its players. entryFeePaid → gross
//     (winners get their share, others $0, no ante deducted).
//   • Nassau: three independent pots (Front 9 / Back 9 / Overall).
//   • Bet per Point: each team settles head-to-head vs every other team on the
//     score differential × bet; a team's net is split equally among its
//     players. (No ante, so entryFeePaid doesn't apply here.)
//
// Standings runners produce the per-segment team scores; this only settles money.

import { Player } from "../types";

export interface TeamSegmentScores {
  groupId: string;
  groupName?: string;
  players: Player[];
  // Combined team score for each segment (strokes for Alliance/Cha Cha Cha,
  // points for Stableford). `overall` = all 18; front/back = holes 1–9 / 10–18.
  overall: number;
  front: number;
  back: number;
  holesPlayed: number;
}

export interface TeamBetOptions {
  overallBet?: number;
  betPerPoint?: number;
  useBetPerPoint?: boolean;
  frontNineBet?: number;
  backNineBet?: number;
  nassau?: boolean;
  entryFeePaid?: boolean;
}

export interface TeamBetResult {
  // Signed money per player id.
  payouts: Record<string, number>;
  // Signed money per group id (sum of its players).
  teamPayouts: Record<string, number>;
  // Whether any bet is configured (drives whether the board shows money).
  hasAnyBet: boolean;
}

export function settleTeamGame(
  teams: TeamSegmentScores[],
  options: TeamBetOptions,
  lowerWins: boolean,
): TeamBetResult {
  const payouts: Record<string, number> = {};
  for (const t of teams) {
    for (const p of t.players) payouts[p.id] = payouts[p.id] ?? 0;
  }

  const feePaid = options.entryFeePaid === true;
  const playing = teams.filter((t) => t.holesPlayed > 0);
  const bestOf = (vals: number[]) =>
    lowerWins ? Math.min(...vals) : Math.max(...vals);

  // Pot: winning team(s) split the field's antes.
  const settlePot = (sel: (t: TeamSegmentScores) => number, bet: number) => {
    if (bet <= 0 || playing.length < 1) return;
    const bestVal = bestOf(playing.map(sel));
    const winners = playing.filter((t) => sel(t) === bestVal);
    const totalPlayers = playing.reduce((s, t) => s + t.players.length, 0);
    const pot = totalPlayers * bet;
    const winnerPlayerCount = winners.reduce(
      (s, t) => s + t.players.length,
      0,
    );
    const share = winnerPlayerCount > 0 ? pot / winnerPlayerCount : 0;
    for (const t of playing) {
      const won = sel(t) === bestVal;
      for (const p of t.players) {
        payouts[p.id] += won
          ? feePaid
            ? share
            : share - bet
          : feePaid
            ? 0
            : -bet;
      }
    }
  };

  // Per point: each team vs every other team on the differential × bet; the
  // team's net is split equally among its players.
  const settlePerPoint = (
    sel: (t: TeamSegmentScores) => number,
    bet: number,
  ) => {
    if (bet <= 0 || playing.length < 2) return;
    for (const a of playing) {
      let net = 0;
      for (const b of playing) {
        if (a.groupId === b.groupId) continue;
        const diff = lowerWins ? sel(b) - sel(a) : sel(a) - sel(b);
        net += diff * bet;
      }
      const per = a.players.length > 0 ? net / a.players.length : 0;
      for (const p of a.players) payouts[p.id] += per;
    }
  };

  if (options.nassau === true) {
    settlePot((t) => t.front, Number(options.frontNineBet) || 0);
    settlePot((t) => t.back, Number(options.backNineBet) || 0);
    settlePot((t) => t.overall, Number(options.overallBet) || 0);
  } else if (options.useBetPerPoint === true) {
    settlePerPoint((t) => t.overall, Number(options.betPerPoint) || 0);
  } else {
    settlePot((t) => t.overall, Number(options.overallBet) || 0);
  }

  const teamPayouts: Record<string, number> = {};
  for (const t of teams) {
    teamPayouts[t.groupId] = t.players.reduce(
      (s, p) => s + (payouts[p.id] || 0),
      0,
    );
  }

  const anyBet =
    (Number(options.overallBet) || 0) > 0 ||
    (Number(options.betPerPoint) || 0) > 0 ||
    (Number(options.frontNineBet) || 0) > 0 ||
    (Number(options.backNineBet) || 0) > 0;

  return { payouts, teamPayouts, hasAnyBet: anyBet };
}
